import { selectUnassigned2dPhotos } from "@/modes/sheet-mode/unassigned-2d-photos-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { selectIsUnassigned2dPhotosPanelVisible } from "@/store/ui/ui-selectors";
import {
  convertToDateString,
  FaroButton,
  FaroText,
  grey,
  TextField,
} from "@faro-lotv/flat-ui";
import { compareDateTimes, GUID } from "@faro-lotv/foundation";
import { IElementUnassigned2dPhoto } from "@faro-lotv/ielement-types";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { groupBy } from "es-toolkit";
import { useCallback, useMemo, useState, type JSX } from "react";
import { Unassigned2dPhotosGroupCard } from "./unassigned-2d-photos-group-card";

/** The fixed width of the unassigned 2D photos panel */
const PANEL_WIDTH = 300;

type Unassigned2dPhotosGroupedByDate = {
  /** The date string representing the date that the photos were created */
  date: string;
  /** The photos taken on the given date */
  photos: IElementUnassigned2dPhoto[];
};

/**
 * Groups an array of unassigned 2D photos by their creation date.
 *
 * @param photos - Array of unassigned 2D photo elements to be grouped
 * @param sortDescending - If true, sorts groups in descending order (newest first); if false, sorts in ascending order (oldest first)
 * @returns An array of photo groups, where each group contains a date string and an array of photos created on that date,
 * sorted by date according to the sortDescending parameter
 */
function groupUnassigned2dPhotosByDate(
  photos: IElementUnassigned2dPhoto[],
  sortDescending: boolean,
): Unassigned2dPhotosGroupedByDate[] {
  const groupedPhotos = Object.entries(
    groupBy(photos, (photo) => convertToDateString(photo.createdAt)),
  ).map(([date, photos]) => ({ date, photos }));

  return groupedPhotos.sort((a, b) => {
    // We are comparing the creation time of the first photo in each group to determine the order of the groups.
    // Using the 'date' field could lead to crash as the date format returned by convertToDateString
    // may vary based on locale and may be incompatible with Date parsing that compareDateTimes relies on.
    const result = compareDateTimes(
      a.photos[0].createdAt,
      b.photos[0].createdAt,
    );
    return sortDescending ? -result : result;
  });
}

/**
 * Creates a comparator function for sorting 2D photos by their creation time.
 *
 * @param sortDescending - When `true`, sorts photos in descending order (newest first).
 *                         When `false`, sorts photos in ascending order (oldest first).
 * @returns A comparator function that can be used with `Array.prototype.sort()` to order
 *          photos by their `createdAt` timestamp.
 */
function photosComparatorByCreationTime(
  sortDescending: boolean,
): (
  photoA: IElementUnassigned2dPhoto,
  photoB: IElementUnassigned2dPhoto,
) => number {
  return (
    photoA: IElementUnassigned2dPhoto,
    photoB: IElementUnassigned2dPhoto,
  ) => {
    const result = compareDateTimes(photoA.createdAt, photoB.createdAt);
    return sortDescending ? -result : result;
  };
}

/**
 * Groups photos into clusters based on time proximity.
 *
 * Photos are initially sorted according to the specified order, then grouped into clusters
 * where consecutive photos are within the specified time frame. Each resulting cluster
 * is sorted in ascending order by creation time, regardless of the input sort order.
 *
 * @param groupedPhotos - An object containing photos grouped by date to be clustered
 * @param timeFrameInSeconds - Maximum time difference (in seconds) between consecutive photos in a cluster
 * @param sortDescending - Whether to sort photos in descending order before clustering (true) or ascending (false)
 * @returns An array of photo clusters, where each cluster contains photos within the time frame, sorted by creation time in ascending order
 */
function computeClustersWithinTimeFrame(
  groupedPhotos: Unassigned2dPhotosGroupedByDate,
  timeFrameInSeconds: number,
  sortDescending: boolean,
): IElementUnassigned2dPhoto[][] {
  const clusters: IElementUnassigned2dPhoto[][] = [];
  const sortedPhotos = groupedPhotos.photos.sort(
    photosComparatorByCreationTime(sortDescending),
  );

  let currentCluster: IElementUnassigned2dPhoto[] = [];
  for (const currentPhoto of sortedPhotos) {
    if (currentCluster.length === 0) {
      currentCluster.push(currentPhoto);
      continue;
    }

    const lastPhotoInCluster = currentCluster[currentCluster.length - 1];
    const timeDifferenceInSeconds =
      Math.abs(
        new Date(currentPhoto.createdAt).getTime() -
          new Date(lastPhotoInCluster.createdAt).getTime(),
      ) / 1000;

    if (timeDifferenceInSeconds <= timeFrameInSeconds) {
      currentCluster.push(currentPhoto);
    } else {
      clusters.push(currentCluster);
      currentCluster = [currentPhoto];
    }
  }

  if (currentCluster.length > 0) {
    clusters.push(currentCluster);
  }

  // Inside a cluster, photos should always be sorted in the ascending order of their creation time
  if (sortDescending) {
    clusters.forEach((cluster) => {
      cluster.reverse();
    });
  }
  return clusters;
}

/**
 * @returns A panel that shows unassigned 2d photos
 */
export function Unassigned2dPhotosPanel(): JSX.Element {
  const panelVisible = useAppSelector(selectIsUnassigned2dPhotosPanelVisible);
  const unassigned2dPhotos = useAppSelector(selectUnassigned2dPhotos());
  const [sortedInDescendingOrder, setSortedInDescendingOrder] = useState(true);
  const defaultTimeFrameInMinutes = 5;
  const [timeFrameInSeconds, setTimeFrameInSeconds] = useState(
    defaultTimeFrameInMinutes * 60,
  );
  const [selectedPhotos, setSelectedPhotos] = useState<GUID[]>([]);

  // Update the selection of photos
  const updateSelection = useCallback(
    (photoId: GUID, shiftKey: boolean) => {
      setSelectedPhotos((prevSelected) => {
        const isSelected = prevSelected.includes(photoId);

        // If Shift is pressed, toggle selection
        if (shiftKey) {
          if (isSelected) {
            return prevSelected.filter((id) => id !== photoId);
          }
          return [...prevSelected, photoId];
        }

        // If Shift is not pressed, select only the clicked photo
        if (isSelected && prevSelected.length === 1) {
          // No need to update selection if the photo is already the only one selected
          return prevSelected;
        }
        return [photoId];
      });
    },
    [setSelectedPhotos],
  );

  const groupedPhotosByDate = useMemo(
    () =>
      groupUnassigned2dPhotosByDate(
        unassigned2dPhotos,
        sortedInDescendingOrder,
      ),
    [unassigned2dPhotos, sortedInDescendingOrder],
  );

  const unassigned2dPhotosGroups = useMemo(
    () =>
      groupedPhotosByDate.map((group) => ({
        name: group.date,
        clusters: computeClustersWithinTimeFrame(
          group,
          timeFrameInSeconds,
          sortedInDescendingOrder,
        ),
      })),
    [groupedPhotosByDate, timeFrameInSeconds, sortedInDescendingOrder],
  );

  const onTimeIntervalChange = useCallback((newValue: string) => {
    const value = parseInt(newValue, 10);
    if (!isNaN(value) && value > 0) {
      setTimeFrameInSeconds(value * 60);
    }
  }, []);

  return (
    <Box
      component="div"
      sx={{
        width: panelVisible ? PANEL_WIDTH : 0,
        flexShrink: 0,
        visibility: panelVisible ? "visible" : "hidden",
        display: "flex",
        flexDirection: "column",
        pl: panelVisible ? 2 : 0,
        pt: 2.5,
        border: panelVisible ? 2 : 0,
        borderColor: grey[100],
      }}
    >
      <Stack direction="row" sx={{ alignItems: "center", gap: 3, mb: 2 }}>
        <FaroText variant="heading14">Unassigned photos</FaroText>
        <FaroButton
          size="s"
          onClick={() => setSortedInDescendingOrder(!sortedInDescendingOrder)}
        >
          Sort {sortedInDescendingOrder ? "↓" : "↑"}
        </FaroButton>
      </Stack>
      <TextField
        type="number"
        label="Time Interval (minutes)"
        placeholder="Enter a time interval"
        text={(timeFrameInSeconds / 60).toString()}
        onTextChanged={onTimeIntervalChange}
      />
      <Stack
        direction="column"
        sx={{
          gap: 3,
          overflowY: "auto",
          flex: 1,
          alignItems: "flex-start",
          mt: 4,
        }}
      >
        {unassigned2dPhotosGroups.map((group) => (
          <Unassigned2dPhotosGroupCard
            key={group.name}
            name={group.name}
            clusters={group.clusters}
            selectedPhotos={selectedPhotos}
            updateSelection={(id, event) =>
              updateSelection(id, !!event?.shiftKey)
            }
          />
        ))}
      </Stack>
    </Box>
  );
}
